import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Clock, DollarSign, BookOpen, Database, TrendingUp, Layers, Brain, MessageSquare, Monitor, Server, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  "data-engineering": Database, "supervised-algorithm": TrendingUp, "unsupervised-algorithm": Layers,
  "deep-learning": Brain, "natural-language-processing": MessageSquare, "ai-native-app-dev": Monitor,
  "frontend-development": Monitor, "backend-development": Server,
};

export function CourseCatalog() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/courses`).then(r => { if (Array.isArray(r.data)) setCourses(r.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-300">Loading courses...</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Courses — PG Diploma in AI, ML & Data Science"
        description="6 hands-on courses covering Data Engineering, Supervised & Unsupervised ML, Deep Learning, NLP, and AI-Native App Development. 20 hours each, $595/course."
        path="/courses"
      />
      <div className="max-w-7xl mx-auto px-6 py-24">
        <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Full Curriculum</p>
        <h1 data-testid="courses-heading" className="text-4xl md:text-5xl font-black tracking-tighter mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          6 Courses to Mastery
        </h1>
        <p className="text-lg text-white/90 mb-16 max-w-2xl">Each course includes 20 hours of expert-led instruction, hands-on projects, quizzes, and real-world case studies.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#262626]">
          {courses.map((course, i) => {
            const Icon = ICONS[course.slug] || BookOpen;
            return (
              <Link
                key={course.slug}
                to={`/courses/${course.slug}`}
                data-testid={`catalog-course-${course.slug}`}
                className="p-8 border-b border-r border-[#262626] bg-[#111111] hover:bg-[#1a1a1a] transition-colors group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-mono font-bold text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>0{course.course_number}</span>
                    <Icon className="w-10 h-10 text-[#0077B6]" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-mono text-zinc-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{course.hours}hrs</span>
                </div>
                <h3 className="text-xl font-semibold tracking-tight mb-2 text-white group-hover:text-[#0077B6] transition-colors" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {course.title}
                </h3>
                <p className="text-base text-white/80 mb-4 line-clamp-2">{course.description}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {(course.skills || []).map((s, j) => (
                    <span key={j} className="text-sm font-mono px-2 py-1 bg-[#0077B6]/10 text-[#0077B6] border border-[#0077B6]/20" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-[#F59E0B]">${course.price}</span>
                  <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-[#F59E0B] group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/courses/${slug}`).then(r => {
      if (r.data && r.data.title && !r.data.detail) setCourse(r.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-300">Loading...</div>;
  if (!course) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <p className="text-2xl font-bold text-[#F59E0B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Course Not Found</p>
        <p className="text-base text-zinc-400 mb-6">This course may have been moved or is unavailable.</p>
        <Link to="/courses" className="brand-btn text-sm px-6 py-3 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to All Courses
        </Link>
      </div>
    </div>
  );

  const Icon = ICONS[slug] || BookOpen;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title={`${course.title} — Course`}
        description={`${course.description?.slice(0, 155) || `Learn ${course.title} at Institute of Analytics USA. ${course.hours} hours of hands-on instruction. $${course.price}.`}`}
        path={`/courses/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": course.title,
          "description": course.description,
          "provider": {"@type": "Organization", "name": "Institute of Analytics USA", "sameAs": "https://americaninstituteofanalytics.com"},
          "offers": {"@type": "Offer", "price": String(course.price || 595), "priceCurrency": "USD"},
          "timeRequired": `PT${course.hours || 20}H`,
          "url": `https://americaninstituteofanalytics.com/courses/${slug}`
        }}
      />
      <div className="max-w-5xl mx-auto px-6 py-24">
        <Link to="/courses" className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#0077B6] mb-8 transition-colors" data-testid="back-to-courses">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Courses
        </Link>
        
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-[#0077B6]/10 flex items-center justify-center border border-[#0077B6]/20">
            <Icon className="w-14 h-14 text-[#0077B6]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xl font-mono font-bold text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Course 0{course.course_number}</p>
            <h1 data-testid="course-title" className="text-3xl md:text-4xl font-bold tracking-tight text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{course.title}</h1>
          </div>
        </div>

        <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">{course.description}</p>

        <div className="grid grid-cols-3 gap-0 border border-[#262626] mb-12">
          <div className="p-6 border-r border-[#262626] text-center bg-[#111111]">
            <Clock className="w-10 h-10 text-[#F59E0B] mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{course.hours}hrs</p>
            <p className="text-xs text-zinc-300">Instruction</p>
          </div>
          <div className="p-6 border-r border-[#262626] text-center bg-[#111111]">
            <BookOpen className="w-10 h-10 text-[#F59E0B] mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{(course.modules || []).length}</p>
            <p className="text-xs text-zinc-300">Modules</p>
          </div>
          <div className="p-6 text-center bg-[#111111]">
            <DollarSign className="w-10 h-10 text-[#F59E0B] mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>${course.price}</p>
            <p className="text-xs text-zinc-300">Investment</p>
          </div>
        </div>

        <h2 className="text-xl font-semibold tracking-tight mb-6 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Modules</h2>
        <div className="space-y-0 border border-[#262626]">
          {(course.modules || []).map((mod, i) => (
            <div key={i} data-testid={`module-${i}`} className="flex items-start gap-4 p-5 border-b border-[#262626] last:border-b-0 bg-[#111111]">
              <span className="text-xl font-mono font-bold text-[#F59E0B] mt-0.5 w-8 flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{String(i + 1).padStart(2, '0')}</span>
              <p className="text-sm text-zinc-300">{mod}</p>
            </div>
          ))}
        </div>

        {course.skills && (
          <div className="mt-12">
            <h2 className="text-xl font-semibold tracking-tight mb-6 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Skills You'll Gain</h2>
            <div className="flex flex-wrap gap-2">
              {course.skills.map((s, i) => (
                <span key={i} className="flex items-center gap-1.5 text-base font-mono px-3 py-2 bg-[#0077B6]/10 text-[#0077B6] border border-[#0077B6]/20" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {s}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 flex gap-4">
          <Link to="/register" data-testid="enroll-course-btn" className="brand-btn text-base px-8 py-3 flex items-center gap-2">
            Enroll in {course.title} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <Link to={`/learn/${slug}`} data-testid="preview-course-btn" className="border-2 border-[#0077B6] text-[#0077B6] px-8 py-3 text-base font-medium hover:-translate-y-1 hover:shadow-[4px_4px_0px_#CC0000] transition-all duration-150 flex items-center gap-2">
            Preview Course Player <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}
